import React, { ReactNode } from "react";

import { makePatch, makePost } from "@culturehq/client";

import { Form, ModalDialog, StringField, SubmitButton } from "@culturehq/components";
import { FormValues } from "@culturehq/components/dist/components/form/Form";

import { ExternalMapper, ExternalMapperValue } from "chq";
import { useDoor, useTryCatch } from "app/hooks";
import { Trans } from "app/utils";

import ErrorMessage from "components/common/ErrorMessage";
import ChqModalDialog from "components/common/ChqModalDialog";

const ModalTitle: React.FC<{ edit: boolean; externalMapper: ExternalMapper }> = (
  { edit, externalMapper }
) => {
  if (edit) {
    return <Trans>Edit {externalMapper.label}</Trans>;
  }
  return <Trans>Add New {(externalMapper as any)?.label || "Mapper"}</Trans>;
};

interface MapperModalProps {
  children: (onOpen: () => void) => ReactNode;
  externalMapperValue?: ExternalMapperValue;
  externalMapper: ExternalMapper;
  onCreate?: (mapper: ExternalMapperValue) => void;
  onUpdate: (mapper: ExternalMapperValue) => void;
}

const MapperModal: React.FC<MapperModalProps> = ({
  children, externalMapperValue, externalMapper, onCreate, onUpdate
}) => {
  const { open, onOpen, onClose } = useDoor(false);

  const [{ error }, onSubmit] = useTryCatch(
    async (values: FormValues) => {
      const cleanedValues = {
        value: typeof values.value === "string" ? values.value.trim() : values.value ?? ""
      };

      if (externalMapperValue) {
        const response: any = await makePatch(
          `/external_mapper_values/${externalMapperValue.id}`,
          cleanedValues as any
        );
        onUpdate(response.externalMapperValue as ExternalMapperValue);
      } else {
        const response: any = await makePost(
          "/external_mapper_values",
          { ...cleanedValues, externalMapperId: externalMapper.id } as any
        );
        if (onCreate) onCreate(response.externalMapperValue);
      }

      onClose();
    },
    [externalMapperValue, onClose, onCreate, onUpdate]
  );

  if (error && error.status !== 422) {
    throw error;
  }

  return (
    <>
      {children(onOpen)}
      {open && (
        <ChqModalDialog onClose={onClose}>
          <ModalDialog.Heading onClose={onClose}>
            <ModalTitle externalMapper={externalMapper} edit={!!externalMapperValue} />
          </ModalDialog.Heading>
          <ModalDialog.Body>
            {error && <ErrorMessage error={error} />}
            <Form onSubmit={onSubmit as any} initialValues={externalMapperValue}>
              <StringField name="value" autoFocus required>
                <Trans>{externalMapper ? `${externalMapper.label}` : "Mapper Name"}</Trans>
              </StringField>
              <SubmitButton primary>
                {submitting => (submitting ? <Trans>Submitting...</Trans> : <Trans>Save</Trans>)}
              </SubmitButton>
            </Form>
          </ModalDialog.Body>
        </ChqModalDialog>
      )}
    </>
  );
};

export default MapperModal;
